import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const categoryMeta = {
  Politics:      { hindi: 'राजनीति',        color: '#1a1a2e', accent: '#e94560' },
  Business:      { hindi: 'व्यापार',         color: '#0f3460', accent: '#f5a623' },
  Sports:        { hindi: 'खेल',             color: '#1b4332', accent: '#52b788' },
  World:         { hindi: 'अंतर्राष्ट्रीय',  color: '#240046', accent: '#9d4edd' },
  Entertainment: { hindi: 'मनोरंजन',         color: '#7b2d8b', accent: '#f72585' },
  Education:     { hindi: 'शिक्षा',           color: '#023e8a', accent: '#48cae4' },
  Technology:    { hindi: 'टेक्नोलॉजी',      color: '#212529', accent: '#20c997' },
  National:      { hindi: 'राष्ट्रीय',        color: '#7f1d1d', accent: '#f00400' },
  Lifestyle:     { hindi: 'लाइफस्टाइल',      color: '#4a1942', accent: '#ff6b9d' },
  Weather:       { hindi: 'मौसम',            color: '#1e3a5f', accent: '#60b0f4' },
  Law:           { hindi: 'कानून',           color: '#2d1b00', accent: '#c9a84c' },
};

const LIMIT = 9;

function CategoryPage() {
  const { category } = useParams();
  const navigate = useNavigate();

  const [news, setNews] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const meta = categoryMeta[category] || { hindi: category, color: '#1a1a1a', accent: '#f00400' };

  useEffect(() => {
    setPage(1);
    setNews([]);
    setFeatured(null);
    fetchNews(1, true);
    window.scrollTo(0, 0);
  }, [category]);

  const fetchNews = async (pageNum, reset = false) => {
    try {
      setLoading(true);
      const res = await fetch(
        `https://backend-gocq.onrender.com/api/news?category=${encodeURIComponent(category)}&status=published&limit=${LIMIT}&page=${pageNum}`
      );
      const data = await res.json();
      if (data.success) {
        if (pageNum === 1) {
          setFeatured(data.data[0] || null);
          setNews(data.data.slice(1));
        } else {
          setNews(prev => [...prev, ...data.data]);
        }
        setTotalPages(data.pagination?.pages || 1);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchNews(next);
  };

  const getImageUrl = (p) => {
    if (!p) return 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&q=80';
    if (p.startsWith('http')) return p;
    return `https://backend-gocq.onrender.com${p}`;
  };

  const getTimeAgo = (date) => {
    const diff = Date.now() - new Date(date);
    const m = Math.floor(diff / 60000);
    const h = Math.floor(m / 60);
    const d = Math.floor(h / 24);
    if (m < 60) return `${m} मिनट पहले`;
    if (h < 24) return `${h} घंटे पहले`;
    if (d < 7)  return `${d} दिन पहले`;
    return new Date(date).toLocaleDateString('hi-IN');
  };

  const formatViews = (v = 0) => {
    if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
    return v;
  };

  const goToNews = (id) => { navigate(`/news/${id}`); window.scrollTo(0, 0); };

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Tiro+Devanagari+Hindi:ital@0;1&family=Mukta:wght@300;400;600;700;800&display=swap');
        * { font-family: 'Mukta', sans-serif; }
        .hn-font { font-family: 'Tiro Devanagari Hindi', serif; }

        .cat-banner {
          background: linear-gradient(135deg, ${meta.color} 0%, ${meta.color}dd 100%);
          position: relative;
          overflow: hidden;
        }
        .cat-banner::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 80% 50%, ${meta.accent}22 0%, transparent 60%);
        }

        .news-card { transition: transform 0.25s ease, box-shadow 0.25s ease; }
        .news-card:hover { transform: translateY(-5px); box-shadow: 0 20px 40px rgba(0,0,0,0.12); }
        .news-card:hover .card-title { color: ${meta.accent}; }

        .gradient-overlay {
          background: linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.3) 50%, transparent 100%);
        }

        .tag-badge {
          background: ${meta.accent}20;
          color: ${meta.accent};
          border: 1px solid ${meta.accent}44;
        }

        .load-btn {
          background: ${meta.accent};
          transition: opacity 0.2s, transform 0.2s;
        }
        .load-btn:hover { opacity: 0.88; transform: translateY(-2px); }

        .skeleton {
          background: linear-gradient(90deg, #e5e7eb 25%, #f9fafb 50%, #e5e7eb 75%);
          background-size: 200% 100%;
          animation: shimmer 1.4s infinite;
        }
        @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      `}</style>

      {/* ── Banner ── */}
      <div className="cat-banner py-12 px-4">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex items-center gap-2 text-white/50 text-sm mb-5">
            <span className="cursor-pointer hover:text-white transition-colors" onClick={() => navigate('/')}>होम</span>
            <span>›</span>
            <span className="text-white font-semibold">{meta.hindi}</span>
          </div>
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <span
                className="inline-block px-3 py-1 rounded-full text-xs font-bold text-white mb-3"
                style={{ backgroundColor: meta.accent }}
              >
                {category.toUpperCase()}
              </span>
              <h1 className="hn-font text-4xl md:text-5xl font-bold text-white">{meta.hindi}</h1>
              <p className="text-white/50 mt-2 text-sm">{meta.hindi} से जुड़ी सभी ताज़ा खबरें</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">

        {/* ── Skeleton ── */}
        {loading && page === 1 && (
          <div className="space-y-8">
            <div className="skeleton rounded-2xl h-80 w-full" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm">
                  <div className="skeleton h-44 w-full" />
                  <div className="p-4 space-y-3">
                    <div className="skeleton h-4 w-full rounded" />
                    <div className="skeleton h-4 w-3/4 rounded" />
                    <div className="skeleton h-3 w-1/3 rounded" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Empty ── */}
        {!loading && !featured && news.length === 0 && (
          <div className="text-center py-24">
            <div className="text-6xl mb-4">📰</div>
            <h2 className="hn-font text-2xl font-bold text-gray-700 mb-2">कोई समाचार नहीं मिला</h2>
            <p className="text-gray-400 mb-6">{meta.hindi} श्रेणी में अभी कोई समाचार उपलब्ध नहीं है।</p>
            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 text-white rounded-full font-bold text-sm load-btn"
            >
              होम पर जाएं
            </button>
          </div>
        )}

        {/* ── Featured ── */}
        {!loading && featured && (
          <div
            className="relative overflow-hidden rounded-2xl shadow-2xl mb-10 cursor-pointer h-72 md:h-96 group"
            onClick={() => goToNews(featured._id)}
          >
            <img
              src={getImageUrl(featured.image)}
              alt={featured.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="gradient-overlay absolute inset-0" />

            <div className="absolute top-4 left-4 flex gap-2">
              <span className="px-3 py-1 text-white text-xs font-bold rounded-full" style={{ backgroundColor: meta.accent }}>
                {category}
              </span>
              <span className="px-3 py-1 bg-white/20 backdrop-blur text-white text-xs font-bold rounded-full">
                ⭐ फीचर्ड
              </span>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <h2 className="hn-font text-2xl md:text-3xl font-bold leading-tight mb-2">
                {featured.title}
              </h2>
              {featured.description && (
                <p className="text-white/75 text-sm mb-3 line-clamp-2">{featured.description}</p>
              )}
              <div className="flex items-center gap-4 text-white/55 text-xs flex-wrap">
                <span>🕐 {getTimeAgo(featured.createdAt)}</span>
                <span>👁 {formatViews(featured.views)} व्यूज</span>
                <span>✍️ {featured.author || 'Admin'}</span>
              </div>
            </div>
          </div>
        )}

        {/* ── Grid ── */}
        {news.length > 0 && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-7 rounded-full" style={{ backgroundColor: meta.accent }} />
              <h2 className="hn-font text-2xl font-bold text-gray-900">सभी समाचार</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {news.map((item) => (
                <div
                  key={item._id}
                  className="news-card bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 cursor-pointer"
                  onClick={() => goToNews(item._id)}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={getImageUrl(item.image)}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="tag-badge px-2.5 py-1 rounded-full text-xs font-semibold">
                        {item.type === 'trending' ? '🔥 ट्रेंडिंग' :
                         item.type === 'top'      ? '⭐ टॉप' :
                         item.type === 'latest'   ? '🆕 ताज़ा' : category}
                      </span>
                    </div>
                  </div>

                  <div className="p-4">
                    <h3 className="hn-font card-title text-base font-bold text-gray-900 line-clamp-3 leading-snug mb-3 transition-colors">
                      {item.title}
                    </h3>
                    {item.description && (
                      <p className="text-gray-400 text-xs line-clamp-2 mb-3 leading-relaxed">
                        {item.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between text-xs text-gray-400 pt-3 border-t border-gray-50">
                      <span>🕐 {getTimeAgo(item.createdAt)}</span>
                      <span>👁 {formatViews(item.views)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Load More */}
            {page < totalPages && (
              <div className="text-center mt-10">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="load-btn text-white px-10 py-3 rounded-full font-bold text-sm disabled:opacity-50 flex items-center gap-2 mx-auto"
                >
                  {loading
                    ? <><svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> लोड हो रहा है...</>
                    : 'और समाचार देखें'}
                </button>
              </div>
            )}
          </>
        )}

        {/* Pagination loading */}
        {loading && page > 1 && (
          <div className="text-center py-8">
            <svg className="animate-spin w-8 h-8 mx-auto" style={{ color: meta.accent }} fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
          </div>
        )}
      </div>
    </div>
  );
}

export default CategoryPage;